<template>
  <div class="funciones-adicionales">
    <data-table
      v-model:editingRows="filasEnEdicion"
      :value="lista"
      editMode="row"
      dataKey="Id"
      showGridlines
      @row-edit-save="guardarFila"
      @row-edit-cancel="cancelarFila"
    >
      <column-element
        v-if="puedeEditar"
        bodyStyle="width: 42px; white-space: nowrap"
        headerStyle="width: 42px; white-space: nowrap"
        headerClass="column-button"
      >
        <template #header>
          <button-element
            icon="pi pi-plus"
            class="p-button-primary border-circle"
            style="width: 30px; height: 30px"
            :disabled="hayFilaNueva"
            @click="crearFila"
          />
        </template>
        <template #body="{ data, index }">
          <button-element
            icon="pi pi-trash"
            class="p-button-secondary p-button-text border-circle"
            style="width: 30px; height: 30px"
            :disabled="hayEdicion"
            @click="eliminarFila(data, index)"
          />
        </template>
      </column-element>
      <column-element field="IdFuncionAdicional" header="Función adicional">
        <template #body="{ data }">
          {{ descripcionDe(opciones.FuncionesAdicionales, data.IdFuncionAdicional) }}
        </template>
        <template #editor="{ data, field }">
          <dropdown-element
            v-model="data[field]"
            filter
            :options="funcionesDisponibles(data)"
            optionLabel="Descripcion"
            optionValue="Id"
            placeholder="Seleccionar función"
          />
        </template>
      </column-element>
      <column-element field="IdFuncionAdicionalEstado" header="Estado">
        <template #body="{ data }">
          {{ descripcionDe(opciones.Estados, data.IdFuncionAdicionalEstado) }}
        </template>
        <template #editor="{ data, field }">
          <dropdown-element
            v-model="data[field]"
            :options="opciones.Estados"
            optionLabel="Descripcion"
            optionValue="Id"
            placeholder="Seleccionar estado"
          />
        </template>
      </column-element>
      <column-element
        v-if="puedeEditar && lista.length"
        :rowEditor="true"
        style="min-width: 85px; width: 85px"
        bodyStyle="text-align: center"
      />
      <template #empty>No hay funciones adicionales asignadas...</template>
    </data-table>
  </div>
</template>

<script setup>
import { computed, nextTick, ref, watch } from "vue";
import { useStore } from "vuex";
import { useToast } from "primevue/usetoast";
import { alertStatus } from "@/helpers/alertStatus";
import * as alerts from "@/helpers/alertMessages";
import {
  actualizarLicenciaFuncionAdicional,
  crearLicenciaFuncionAdicional,
  eliminarLicenciaFuncionAdicional,
} from "../services/gfuncionesAdicionales";

const props = defineProps({
  idLicencia: {
    type: Number,
    required: true,
  },
  funcionesAdicionales: {
    type: Array,
    default: () => [],
  },
  puedeEditar: {
    type: Boolean,
    default: false,
  },
  opciones: {
    type: Object,
    default: () => ({ FuncionesAdicionales: [], Estados: [] }),
  },
});

const store = useStore();
const toast = useToast();
const lista = ref([]);
const filasEnEdicion = ref([]);

const hayFilaNueva = computed(() => lista.value.some((fila) => !fila.Id));
const hayEdicion = computed(() => filasEnEdicion.value.length > 0);

const descripcionDe = (items, id) =>
  items?.find((item) => item.Id === id)?.Descripcion ?? "";

const funcionesDisponibles = (fila) =>
  props.opciones.FuncionesAdicionales.filter((funcion) => {
    if (funcion.Id === fila.IdFuncionAdicional) return true;

    return !lista.value.some(
      (item) => item.Id !== fila.Id && item.IdFuncionAdicional === funcion.Id
    );
  });

const crearFila = () => {
  if (hayFilaNueva.value) return;

  const fila = {
    Id: null,
    IdFuncionAdicional: null,
    IdFuncionAdicionalEstado: null,
  };
  lista.value.push(fila);
  filasEnEdicion.value = [...filasEnEdicion.value, fila];
};

const camposCompletos = (fila) =>
  Boolean(fila.IdFuncionAdicional && fila.IdFuncionAdicionalEstado);

const reabrirEdicion = (fila) => {
  filasEnEdicion.value = [...filasEnEdicion.value, fila];
};

const guardarFila = async ({ newData, data, index }) => {
  if (!camposCompletos(newData)) {
    toast.add({
      severity: "warn",
      summary: "",
      detail: "Seleccione la función adicional y el estado.",
      life: 3500,
    });
    reabrirEdicion(data);
    return;
  }

  store.commit("setIsLoading", true);
  const esNueva = !newData.Id;
  const respuesta = esNueva
    ? await crearLicenciaFuncionAdicional(props.idLicencia, newData)
    : await actualizarLicenciaFuncionAdicional(props.idLicencia, newData);
  store.commit("setIsLoading", false);

  if (respuesta.status === 201 || respuesta.status === 200) {
    lista.value[index] = respuesta.data;
    toast.add(esNueva ? alerts.creado : alerts.actualizado);
    return;
  }

  alertStatus(respuesta.status, respuesta.data);
  if (esNueva) Object.assign(data, newData);
  reabrirEdicion(data);
};

const cancelarFila = ({ data }) => {
  if (data?.Id) return;

  nextTick(() => {
    const index = lista.value.findIndex((fila) => !fila.Id);
    if (index !== -1) lista.value.splice(index, 1);
  });
};

const eliminarFila = async (fila, index) => {
  const confirmacion = await alerts.eliminarRegistro();
  if (!confirmacion?.isConfirmed) return;

  if (!fila.Id) {
    lista.value.splice(index, 1);
    return;
  }

  store.commit("setIsLoading", true);
  const { data, status } = await eliminarLicenciaFuncionAdicional(
    props.idLicencia,
    fila.Id
  );
  store.commit("setIsLoading", false);

  if (status === 204) {
    lista.value.splice(index, 1);
    toast.add(alerts.eliminado);
    return;
  }

  alertStatus(status, data);
};

watch(
  () => props.funcionesAdicionales,
  (valor) => {
    lista.value = (valor ?? []).map((fila) => ({ ...fila }));
    filasEnEdicion.value = [];
  },
  { immediate: true }
);
</script>

<style lang="scss" scoped>
:deep(.p-datatable) {
  .p-datatable-wrapper {
    margin-bottom: 10px !important;
    box-shadow: none !important;
  }
  .p-datatable-thead > tr > th {
    background-color: #f9f9f9 !important;
    border: 1px solid lightgray !important;
  }
  .column-button .p-column-header-content {
    justify-content: center;
  }
  .p-dropdown {
    min-width: 220px;
  }
}
</style>
